// ============================================================
// IDLE LIFE (plan body-map S3; the bench, ruled 2026-10-08): a figure on the
// plate is never quite still - a small sway, weight shifting, breath, now and
// then a settle - each kind its own way: a person stands, a beast prowls
// planted, a machine holds then eases to a new set like a servo, the daemon
// floats. It moves the WHOLE figure only (a part moved alone opens seams),
// eases in and out, gives way to every hit, and stops for the states whose
// stillness means something. The goo's idle is its body's (gooLive).
//
// Pure. Zero React dependency. Distances in frame units, rotation in degrees.
// ============================================================

import type { IdleKind } from './figureArt';
import type { PlateState } from './plateState';
import { hash01 } from './hash';
import { scatterOf } from '../battlefield/terrainAnimation';

export interface IdleMotion {
  dx: number;
  dy: number;
  /** Degrees, about the feet. */
  rot: number;
  /** The breath: a vertical scale from the feet. */
  sy: number;
}

const wave =
  (T: number) =>
  (f: number, ph = 0) =>
    Math.sin(T * f + ph);

/** The servo's next set: a fixed hash in [-0.5, 0.5), the same every time. */
const hash = (k: number, step: number) => hash01(k, step) - 0.5;

/** Sway, weight from foot to foot, breath, an occasional settle; heavier and slower for the giant. */
function stand(T: number, heavy: boolean): IdleMotion {
  const w = wave(T);
  const k = heavy ? 0.7 : 1;
  return {
    dx: 10 * w(0.21 * k, 2) + 4 * Math.pow(Math.max(0, w(0.07, 3)), 8),
    dy: 0,
    rot: 0.7 * w(0.45 * k) + 0.3 * w(0.17, 1),
    sy: 1 + 0.006 * w(1.3 * k),
  };
}

/** Planted and restless: a slight pacing shift and a quick breath, no bob. */
function prowl(T: number): IdleMotion {
  const w = wave(T);
  return { dx: 6 * w(0.35) + 2 * w(0.9, 1), dy: 0, rot: 0.35 * w(0.35, 0.5), sy: 1 + 0.01 * w(2.6) };
}

/** Deliberate: it holds, then eases to a new set (a fifth of each 3.6 s), and holds - not a spasm. */
function twitch(T: number): IdleMotion {
  const T2 = T / 3.6;
  const step = Math.floor(T2);
  const f = T2 - step;
  const ease = f < 0.18 ? (1 - Math.cos((f / 0.18) * Math.PI)) / 2 : 1;
  const set = (k: number) => hash(k, step - 1) + (hash(k, step) - hash(k, step - 1)) * ease;
  return { dx: 10 * set(1), dy: 0, rot: 0.5 * set(2), sy: 1 };
}

/** The daemon floats, bobbing and turning a little. */
function float(T: number): IdleMotion {
  const w = wave(T);
  return { dx: 8 * w(0.23, 2), dy: 22 * w(0.5), rot: 1 * w(0.3, 1), sy: 1 };
}

const KINDS: Record<IdleKind, (T: number, heavy: boolean) => IdleMotion> = { stand, prowl, twitch, float };

/** A figure's idle at `T` seconds into its own rhythm (`heavy`: the giant's slower sway). */
export function idleMotion(kind: IdleKind, T: number, heavy: boolean): IdleMotion {
  return KINDS[kind](T, heavy);
}

/** The motion scaled by how far idle has eased in (0 still .. 1 full). */
export const scaleIdle = (m: IdleMotion, amp: number): IdleMotion => ({
  dx: m.dx * amp,
  dy: m.dy * amp,
  rot: m.rot * amp,
  sy: 1 + (m.sy - 1) * amp,
});

/** The states whose stillness means something (and death): no idle under them. */
export const idleStill = (s: PlateState) =>
  s.dead || s.paralysed || s.unconscious || s.helpless || s.immob || s.stun || s.dying;

/** A figure's own place in its rhythm, from a name (each plate's id): figures side by side never sway in step. */
export const idleSeed = (name: string) => scatterOf(name) * 100;
